import { Injectable } from '@angular/core';
import {ObjectService} from "./abstractions/objects/object.service.abstract";
import {Observable} from "rxjs";
import {AssistantRegressionTestCase, IAssistantOptions} from "../../models/assistant-options.model";
import {
  AssistantFeedBindingValidationResult,
  AssistantDraftTestCase,
  AssistantVersionOptions,
  PublishAssistantDraftResponse,
  UpdateAssistantDraftRequest
} from "../../models/assistant-version.model";

@Injectable({
  providedIn: 'root'
})
export class AssistantsService  extends ObjectService<IAssistantOptions> {

  protected api = '/assistants/'; // Base URL for the client API

  override deleteAsync(id: string): Observable<IAssistantOptions> {
    return this.http.delete<IAssistantOptions>(this.getEndpoint() + id + '/delete');
  }

  proposeImprovementsToAssistantAsync(assistantId: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(this.getEndpoint() + assistantId + '/propose-improvements');
  }

  reportAsync(id: string, startDate?: string, endDate?: string): Observable<any> {
    return this.http.get<any>(this.getEndpoint() + id + '/report?startDate=' + startDate + '&endDate=' + endDate);
  }

  reportAllAsync(startDate?: string, endDate?: string): Observable<any> {
    return this.http.get<any>(this.getEndpoint() + 'report?startDate=' + startDate + '&endDate=' + endDate);
  }

  getVersionsAsync(assistantId: string): Observable<AssistantVersionOptions[]> {
    return this.http.get<AssistantVersionOptions[]>(`${this.getEndpoint()}${assistantId}/versions`);
  }

  getRegressionTestsAsync(assistantId: string): Observable<AssistantRegressionTestCase[]> {
    return this.http.get<AssistantRegressionTestCase[]>(`${this.getEndpoint()}${assistantId}/versions/regression-tests`);
  }

  validateFeedBindingAsync(
    assistantId: string,
    assistant: IAssistantOptions
  ): Observable<AssistantFeedBindingValidationResult> {
    return this.http.post<AssistantFeedBindingValidationResult>(
      `${this.getEndpoint()}${assistantId}/feed-binding-validation`,
      assistant
    );
  }

  saveRegressionTestsAsync(
    assistantId: string,
    testCases: AssistantRegressionTestCase[]
  ): Observable<AssistantRegressionTestCase[]> {
    return this.http.post<AssistantRegressionTestCase[]>(
      `${this.getEndpoint()}${assistantId}/versions/regression-tests`,
      {testCases}
    );
  }

  createDraftAsync(assistantId: string, sourceVersionId?: string): Observable<AssistantVersionOptions> {
    return this.http.post<AssistantVersionOptions>(`${this.getEndpoint()}${assistantId}/versions/drafts`, {
      sourceVersionId
    });
  }

  updateDraftAsync(
    assistantId: string,
    draftId: string,
    request: UpdateAssistantDraftRequest
  ): Observable<AssistantVersionOptions> {
    return this.http.post<AssistantVersionOptions>(
      `${this.getEndpoint()}${assistantId}/versions/drafts/${draftId}/update`,
      request
    );
  }

  validateDraftAsync(assistantId: string, draftId: string): Observable<AssistantVersionOptions> {
    return this.http.post<AssistantVersionOptions>(
      `${this.getEndpoint()}${assistantId}/versions/drafts/${draftId}/validate`,
      {}
    );
  }

  runDraftTestsAsync(
    assistantId: string,
    draftId: string,
    testCases?: AssistantDraftTestCase[]
  ): Observable<AssistantVersionOptions> {
    return this.http.post<AssistantVersionOptions>(
      `${this.getEndpoint()}${assistantId}/versions/drafts/${draftId}/tests`,
      {testCases}
    );
  }

  publishDraftAsync(assistantId: string, draftId: string): Observable<PublishAssistantDraftResponse> {
    return this.http.post<PublishAssistantDraftResponse>(
      `${this.getEndpoint()}${assistantId}/versions/drafts/${draftId}/publish`,
      {}
    );
  }

  rollbackAsync(assistantId: string, versionId: string): Observable<PublishAssistantDraftResponse> {
    return this.http.post<PublishAssistantDraftResponse>(
      `${this.getEndpoint()}${assistantId}/versions/${versionId}/rollback`,
      {}
    );
  }
}
